/**
 * Admin Portal → Codes → Offers & discounts — `/admin/codes/offers` (Codes PR-8, on
 * `GET /api/admin/codes/offers`, Codes PR-7).
 *
 * The discount menu: every (discount, length) pair, whether it is on, how many live links use it and
 * how many people hold it. Per store product: its offer-slot meter, each slot named by its owner (a
 * partner discount, a Summer50 reward offer, a promotion the store lists that Coach Cedar didn't make).
 *
 * Read-only here for every role. Switching an offer on or off, and registering its store offer id,
 * stays on Store setup (super_admin).
 *
 * Web-only (the admin SPA).
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminCard, Badge } from '../../../components/admin/ui';
import { SLOT_EXPLAINER, slotHolderLabel, slotLine } from '../../../components/admin/storeAccessLogic';
import { hint, td, th } from '../partners/PartnersShared';
import { CodesAdmin } from './codesAdminService';
import { CODES_ROUTES, comboLabel, slotText, type OffersView } from './codesAreaLogic';
import { CodesHeader, ErrorLine, PAGE, readModelError, useCodesRole } from './CodesArea';

export function CodesOffers() {
  const { superAdmin } = useCodesRole();
  const [view, setView] = useState<OffersView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    CodesAdmin.offers().then((r) => { if (live) setView(r); }).catch((e) => { if (live) setError(readModelError(e)); });
    return () => { live = false; };
  }, []);

  const combos = view?.combos ?? [];
  const slots = view?.slots ?? [];
  return (
    <div style={PAGE}>
      <CodesHeader
        title="Offers & discounts"
        subtitle="The discounts a code can carry, who uses each one, and how many of each store product's offer slots are taken."
        actions={superAdmin ? <Link to={CODES_ROUTES.storeSetup} style={{ fontSize: 13 }}>Change offers on Store setup →</Link> : undefined}
      />
      <ErrorLine text={error} />
      {!superAdmin && <p style={{ ...hint, marginTop: 0 }}>Read-only here. A super admin switches offers on or off on Store setup.</p>}
      {view && (
        <>
          <AdminCard style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, margin: '0 0 8px' }}>Discount menu</h2>
            {combos.length === 0 ? <div style={hint}>No discounts on the menu yet.</div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="combos">
                <thead>
                  <tr>
                    <th style={th}>Discount</th><th style={th}>On the menu</th>
                    <th style={th}>Live links (monthly / annual)</th><th style={th}>People holding it (monthly / annual)</th>
                  </tr>
                </thead>
                <tbody>
                  {combos.map((c) => (
                    <tr key={`${c.pct}-${c.months}`}>
                      <td style={td}>{comboLabel(c)}</td>
                      <td style={td}><Badge status={c.status === 'active' ? 'success' : 'neutral'}>{c.status === 'active' ? 'On' : 'Retired'}</Badge></td>
                      <td style={td}>{c.live_tags_monthly} / {c.live_tags_annual}</td>
                      <td style={td}>{c.grants_monthly} / {c.grants_annual}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </AdminCard>
          <AdminCard>
            <h2 style={{ fontSize: 16, margin: '0 0 4px' }}>Offer slots per store product</h2>
            <p style={{ ...hint, marginTop: 0 }}>{SLOT_EXPLAINER}</p>
            {slots.length === 0 ? <div style={hint}>No store products are set up yet.</div> : (
              <div style={{ display: 'grid', gap: 12 }} data-testid="slots">
                {slots.map((s) => (
                  <div key={`${s.store}:${s.product_id}`} style={{ borderTop: '1px solid var(--border-subtle, #f3f4f6)', paddingTop: 8 }}>
                    <div style={{ fontSize: 13 }}>
                      <b>{s.product_id}</b> <span style={hint}>({s.store === 'app_store' ? 'App Store' : 'Google Play'})</span> — {slotText(s)}
                      {s.stale && <span style={{ ...hint, marginLeft: 6 }}>(the last store read is old: these are Coach Cedar&apos;s records)</span>}
                    </div>
                    {slotLine(s) && <div style={hint}>{slotLine(s)}</div>}
                    {(s.owners ?? []).length > 0 && (
                      <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12 }}>
                        {(s.owners ?? []).map((h, i) => <li key={i}>{slotHolderLabel(h)}</li>)}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </AdminCard>
        </>
      )}
    </div>
  );
}

export default CodesOffers;
